import 'react-calendar/dist/Calendar.css';

import dayjs from 'dayjs';
import React, { useState } from 'react';
import Calendar from 'react-calendar';
import { Value } from 'react-calendar/dist/cjs/shared/types';
import styled from 'styled-components';

import { Button, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { icons } from '../../themes/icons';

interface ModalBodyProps {
  setModalOpen: (open: boolean) => void;
  setFromDate: (date: string | null) => void;
  setToDate: (date: string | null) => void;
  setPriorFrom: (date: string | null) => void;
  setPriorTo: (date: string | null) => void;
}

const ModalBody: React.FC<ModalBodyProps> = ({
  setModalOpen,
  setFromDate,
  setToDate,
  setPriorFrom,
  setPriorTo,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isSelectingStart, setIsSelectingStart] = useState<boolean>(true); // To toggle between start and end date selection
  const [isSelectingEnd, setIsSelectingEnd] = useState<boolean>(false); // To toggle between start and end date selection

  const [startDateIsAfterEndDate, setStartDateIsAfterEndDate] = useState(false);

  const handleDateChange = (date: Date) => {
    if (isSelectingStart) {
      setStartDate(date);
      setStartDateIsAfterEndDate(false);
    } else if (isSelectingEnd) {
      setEndDate(date);
      setStartDateIsAfterEndDate(false);
    }
  };

  const onApply = () => {
    const today = dayjs();
    const startDayjs = dayjs(startDate || '');
    const endDayjs = dayjs(endDate || '');

    // Calculate difference from today
    const startingDifference = today.diff(startDayjs, 'day');
    const endingDifference = today.diff(endDayjs, 'day');

    console.log(startingDifference, endingDifference);

    // Check if start date is after end date
    if (startDayjs.isAfter(endDayjs)) {
      return setStartDateIsAfterEndDate(true);
    }

    // Format the selected start and end dates
    const startFormattedDate = startDate ? startDayjs.format('YYYY-MM-DD') : null;
    const endFormattedDate = endDate ? endDayjs.format('YYYY-MM-DD') : null;

    // Calculate prior dates by subtracting the difference from today (not multiplying by 2)
    const priorStartFormattedDate = startDate
      ? startDayjs.subtract(startingDifference, 'day').format('YYYY-MM-DD')
      : null;
    const priorEndFormattedDate = endDate
      ? endDayjs.subtract(endingDifference, 'day').format('YYYY-MM-DD')
      : null;

    // Set the dates in the state
    setFromDate(startFormattedDate);
    setToDate(endFormattedDate);
    setPriorFrom(priorStartFormattedDate);
    setPriorTo(priorEndFormattedDate);

    // Close the modal
    setModalOpen(false);
  };

  return (
    <ModalBodyContainer>
      <ModalHeader>
        <span>Custom Dates</span>
        <button
          onClick={() => {
            setModalOpen(false);
          }}
        >
          <CloseIcon />
        </button>
      </ModalHeader>

      <DateButtons>
        <StartDate
          isActive={isSelectingStart}
          onClick={() => {
            setIsSelectingStart(true);
            setIsSelectingEnd(false);
          }}
        >
          <icons.CalendarIcon />
          <span>{startDate ? dayjs(startDate).format('YYYY-MM-DD') : 'Start Date'}</span>
        </StartDate>
        <StartDate
          isActive={isSelectingEnd}
          onClick={() => {
            setIsSelectingStart(false);
            setIsSelectingEnd(true);
          }}
        >
          <icons.CalendarIcon />
          <span>{endDate ? dayjs(endDate).format('YYYY-MM-DD') : 'End Date'}</span>
        </StartDate>
      </DateButtons>

      <CalendarContainer>
        <Calendar
          maxDate={new Date()}
          onChange={(value: Value) => {
            handleDateChange(value as Date);
          }}
          value={isSelectingStart ? startDate : endDate}
        />

        {startDateIsAfterEndDate && (
          <Typography color="error" style={{ fontSize: '12px' }}>
            Start date should not be greater than End date
          </Typography>
        )}
        <Button
          fullWidth
          style={{ margin: '10px 0' }}
          variant="contained"
          color="primary"
          onClick={onApply}
          disabled={!startDate || !endDate}
        >
          Apply
        </Button>
      </CalendarContainer>
    </ModalBodyContainer>
  );
};

export default ModalBody;

// Styled Components
const ModalBodyContainer = styled.div`
  background: white;
  width: 330px;
  height: 544px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 24px 20px;
  border-radius: 8px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  span {
    color: #111111;
    font-size: 18px;
    font-family: Poppins;
    font-weight: 500;
  }
  button {
    border: none;
    background: none;
    cursor: pointer;
  }
`;

const DateButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 26px 0;
`;

const StartDate = styled.button<{ isActive: boolean }>`
  width: 140px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid ${(props) => (props.isActive ? '#3f51b5' : '#e6e6e6')};
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
`;

const CalendarContainer = styled.div`
  .react-calendar {
    border: none;
  }
`;
